/**
 * Type scale. Apply with `textStyle="h2"` etc. (works on Text, Heading, Box).
 * display.* and h1–h4 use Bricolage Grotesque; label/body/caption/numeral use Manrope.
 * `label` is the uppercase eyebrow used for table heads, form labels, eyebrows and folios — never below 0.75rem.
 * `numeral` only sets weight + tabular figures; it inherits size so it can be combined (e.g. in a table cell).
 *
 * Dotted names such as `textStyle="display.xl"` are stored NESTED (`display: { xl }`): Chakra resolves
 * `textStyles.<name>` by splitting the path on dots, so a flat `"display.xl"` key would never be found and
 * the text would silently fall back to body type.
 *
 * Responsive values work too (`textStyle={{ base: "display.md", lg: "display.xl" }}` or an array), as long as
 * each name resolves to a nested key. `display.hero`, `stat.hero` and `stat.kpi` are convenience styles that
 * carry their own breakpoints so callers can pass a single name.
 *
 * textStyle has the lowest priority: any key already set by the element's component theme (e.g. a Table `td`'s
 * fontWeight) wins over it, so set that property directly on such elements.
 */
export const textStyles = {
  display: {
    xl: {
      fontFamily: "heading",
      fontSize: "3.5rem",
      lineHeight: 1,
      fontWeight: 700,
      letterSpacing: "-0.03em",
      fontVariationSettings: '"opsz" 96',
    },
    lg: {
      fontFamily: "heading",
      fontSize: "2.75rem",
      lineHeight: 1,
      fontWeight: 700,
      letterSpacing: "-0.025em",
      fontVariantNumeric: "tabular-nums lining-nums",
      fontVariationSettings: '"opsz" 72',
    },
    md: {
      fontFamily: "heading",
      fontSize: "2rem",
      lineHeight: 1.1,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariationSettings: '"opsz" 48',
    },
    sm: {
      fontFamily: "heading",
      fontSize: "1.75rem",
      lineHeight: 1.1,
      fontWeight: 700,
      letterSpacing: "-0.015em",
      fontVariantNumeric: "tabular-nums lining-nums",
      fontVariationSettings: '"opsz" 48',
    },
    /** Login headline: display.md on phones, display.xl from lg. */
    hero: {
      fontFamily: "heading",
      fontSize: { base: "2rem", lg: "3.5rem" },
      lineHeight: { base: 1.1, lg: 1 },
      fontWeight: 700,
      letterSpacing: { base: "-0.02em", lg: "-0.03em" },
      fontVariationSettings: { base: '"opsz" 48', lg: '"opsz" 96' },
    },
  },
  h1: { fontFamily: "heading", fontSize: "1.5rem", lineHeight: 1.2, fontWeight: 700, letterSpacing: "-0.015em" },
  h2: { fontFamily: "heading", fontSize: "1.375rem", lineHeight: 1.2, fontWeight: 700, letterSpacing: "-0.01em" },
  h3: { fontFamily: "heading", fontSize: "1.125rem", lineHeight: 1.3, fontWeight: 700, letterSpacing: "-0.005em" },
  h4: { fontFamily: "heading", fontSize: "1rem", lineHeight: 1.35, fontWeight: 700 },
  label: {
    fontFamily: "body",
    fontSize: "0.75rem",
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  body: {
    lg: { fontFamily: "body", fontSize: "1.0625rem", lineHeight: 1.55, fontWeight: 500 },
    md: { fontFamily: "body", fontSize: "0.9375rem", lineHeight: 1.55, fontWeight: 500 },
    sm: { fontFamily: "body", fontSize: "0.875rem", lineHeight: 1.5, fontWeight: 500 },
  },
  caption: { fontFamily: "body", fontSize: "0.75rem", lineHeight: 1.4, fontWeight: 600, letterSpacing: "0.01em" },
  numeral: { fontFamily: "body", fontWeight: 600, fontVariantNumeric: "tabular-nums lining-nums" },
  stat: {
    /** Hero stat value (overdue balance): display.md on phones, display.lg from lg. */
    hero: {
      fontFamily: "heading",
      fontSize: { base: "2rem", lg: "2.75rem" },
      lineHeight: { base: 1.1, lg: 1 },
      fontWeight: 700,
      letterSpacing: { base: "-0.02em", lg: "-0.025em" },
      fontVariantNumeric: "tabular-nums lining-nums",
      fontVariationSettings: { base: '"opsz" 48', lg: '"opsz" 72' },
    },
    /** KPI stat value: h1 on phones, display.sm from md. */
    kpi: {
      fontFamily: "heading",
      fontSize: { base: "1.5rem", md: "1.75rem" },
      lineHeight: { base: 1.2, md: 1.1 },
      fontWeight: 700,
      letterSpacing: "-0.015em",
      fontVariantNumeric: "tabular-nums lining-nums",
      fontVariationSettings: { base: "normal", md: '"opsz" 48' },
    },
  },
};
